import { useInvitationRules, useMarketConfigs } from '../../features/settings';
import { formatDateTime } from '../../lib/format';

const MAX_STEPS = 6;
const MAX_RULES = 10;

export function InvitationRulesPage() {
  const markets = useMarketConfigs();
  const {
    data,
    loading,
    saving,
    error,
    selectedMarketId,
    steps,
    rules,
    selectMarket,
    updateStep,
    addStep,
    removeStep,
    updateRule,
    addRule,
    removeRule,
    save,
    reload,
  } = useInvitationRules();

  if (loading || markets.loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (error || markets.error) {
    return (
      <div className="py-20 text-center">
        <p className="text-sm text-red-500 mb-3">{error || markets.error}</p>
        <button onClick={() => { reload(); markets.reload(); }} className="text-sm text-primary hover:underline">
          重试
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-8">
      <p className="text-sm text-gray-500">
        配置移动端推广帮助弹层展示的步骤与规则文案。这里保存后，线上 App 会优先读取运行时配置。
      </p>

      <label className="block max-w-xs space-y-1">
        <span className="text-sm font-medium text-gray-700">配置地区</span>
        <select
          value={selectedMarketId}
          onChange={(e) => selectMarket(e.target.value)}
          className="h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          {markets.items.map((market) => (
            <option key={market.market_id} value={market.market_id}>
              {market.market_id} · {market.name}
            </option>
          ))}
        </select>
      </label>

      <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4 text-sm text-amber-900">
        建议文案口径统一为：被邀请人完成首个有效任务后发一次固定奖励，后续有效任务再按比例持续返佣。
      </div>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">推广步骤</h2>
            <p className="mt-1 text-sm text-gray-500">用于说明用户从分享、注册到奖励生效的流程。</p>
          </div>
          <button
            onClick={addStep}
            disabled={steps.length >= MAX_STEPS}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            新增步骤
          </button>
        </div>

        <div className="space-y-4">
          {steps.map((step, index) => (
            <div key={`step-${index}`} className="rounded-xl border border-gray-200 bg-white p-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">步骤 {index + 1}</span>
                <button
                  onClick={() => removeStep(index)}
                  disabled={steps.length <= 1}
                  className="text-sm text-red-500 hover:text-red-600 disabled:opacity-40"
                >
                  删除
                </button>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">标题</label>
                  <input
                    type="text"
                    value={step.title}
                    onChange={(e) => updateStep(index, 'title', e.target.value)}
                    className="h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">说明</label>
                  <textarea
                    rows={3}
                    value={step.description}
                    onChange={(e) => updateStep(index, 'description', e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">推广规则</h2>
            <p className="mt-1 text-sm text-gray-500">用于说明邀请关系、固定奖励和持续返佣的生效条件。</p>
          </div>
          <button
            onClick={addRule}
            disabled={rules.length >= MAX_RULES}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            新增规则
          </button>
        </div>

        <div className="space-y-3">
          {rules.map((rule, index) => (
            <div key={`rule-${index}`} className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">规则 {index + 1}</span>
                <button
                  onClick={() => removeRule(index)}
                  disabled={rules.length <= 1}
                  className="text-sm text-red-500 hover:text-red-600 disabled:opacity-40"
                >
                  删除
                </button>
              </div>
              <textarea
                rows={3}
                value={rule}
                onChange={(e) => updateRule(index, e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
            </div>
          ))}
        </div>
      </section>

      <div>
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-primary-hover disabled:opacity-50 transition-colors"
        >
          {saving && (
            <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          )}
          保存
        </button>
      </div>

      {data && data.updated_at && (
        <div className="pt-4 border-t border-gray-200 text-xs text-gray-400 space-y-1">
          <p>最后更新：{formatDateTime(data.updated_at)}</p>
          {data.updated_by != null && <p>操作人 ID：{data.updated_by}</p>}
        </div>
      )}
    </div>
  );
}
